import { defineStore } from "pinia";
import { ref } from "vue";
import { API } from "@/queries/index";
import { AxiosError } from "axios";
import { APIResponse, APIResponseError } from "@/types/api";
import { handleResponse, handleResponseError } from "@/utils/api";
import { AccessPermissionDataPayload, AccessPermissionDataRolePayload, AccessPermissionDataRoleResponse } from "@/types/interface/access-permission";

export const useAccessPermissionStore = defineStore("accessPermissionStore", () => {
  const access = ref<AccessPermissionDataRoleResponse>();
  const accessPermission = ref<AccessPermissionDataRoleResponse>();
  const listAccess = ref();

  function initAccess(data: any) {
    access.value = data?.data;
  }

  function initListAccess(data: any) {
    listAccess.value = data?.data;
  }

  function addNewAccessPermission(data: any) {
    accessPermission.value = data;
  }

  async function dispatchListAll(page = 1, limit = 10, queryParam = "") : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.accessPermission.getAccessPermission(page, limit, queryParam);
      if (status === 200 ) {
        initListAccess(data);
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
    return handleResponse(200, null);
  }

  async function dispatchDetail(idAccessPermission = "") : Promise<APIResponse<null>> {
      try {
        const { status, data } = await API.accessPermission.findAccessPermission(idAccessPermission);
        if (status === 200 ) {
          addNewAccessPermission(data);
          return handleResponse(status, data);
        }
      } catch (error) {
        const _error = error as AxiosError<APIResponseError>;
        const errorResponseData = _error.response?.data
        return handleResponseError(errorResponseData)
      }
      return handleResponse(200, null);
    }

    async function dispatchCreate(payload: AccessPermissionDataPayload) : Promise<APIResponse<null>> {
      try {
        const { status, data } = await API.accessPermission.createAccessPermission(payload);
        if (status === 200 ) {
          addNewAccessPermission(data);
          return handleResponse(status, data);
        }
      } catch (error) {
        const _error = error as AxiosError<APIResponseError>;
        const errorResponseData = _error.response?.data
        return handleResponseError(errorResponseData)
      }
      return handleResponse(200, null);
    }

    async function dispatchUpdate(idAccessPermission = "", payload: AccessPermissionDataPayload) : Promise<APIResponse<null>> {
      try {
        const { status, data } = await API.accessPermission.updateAccessPermission(idAccessPermission, payload);
        if (status === 200 ) {
          addNewAccessPermission(data);
          return handleResponse(status, data);
        }
      } catch (error) {
        const _error = error as AxiosError<APIResponseError>;
        const errorResponseData = _error.response?.data
        return handleResponseError(errorResponseData)
      }
      return handleResponse(200, null);
    }

    async function dispatchDelete(idAccessPermission = "") : Promise<APIResponse<null>> {
      try {
        const { status, data } = await API.accessPermission.deleteAccessPermission(idAccessPermission);
        if (status === 200 ) {
          return handleResponse(status, data);
        }
      } catch (error) {
        const _error = error as AxiosError<APIResponseError>;
        const errorResponseData = _error.response?.data
        return handleResponseError(errorResponseData)
      }
      return handleResponse(200, null);
    }

  async function dispatchGetAll(idRole = 1) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.accessPermission.getAccessPermissionByRole(idRole);
      if (status === 200 ) {
        initAccess(data);
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
    return handleResponse(200, null);
  }

  async function dispatchSetRole(payload: AccessPermissionDataRolePayload) : Promise<APIResponse<null>> {
    try {
      const { status, data } = await API.accessPermission.setRole(payload);
      if (status === 200 ) {
        addNewAccessPermission(data);
        return handleResponse(status, data);
      }
    } catch (error) {
      const _error = error as AxiosError<APIResponseError>;
      const errorResponseData = _error.response?.data
      return handleResponseError(errorResponseData)
    }
    return handleResponse(200, null);
  }

  return {
    access,
    accessPermission,
    listAccess,
    dispatchListAll,
    dispatchDelete,
    dispatchDetail,
    dispatchCreate,
    dispatchUpdate,
    initListAccess,
    initAccess,
    dispatchGetAll,
    dispatchSetRole
  };
});